import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { step2Schema, type Step2FormValues } from "@/schemas/onboarding-schema";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useEmailVerification } from "@/hooks/useEmailVerification";
import FormShell from "@/components/form-shell";

export function OnboardingStep2() {
  const { session, updateOnboardingStep } = useOnboarding("step2");
  const { isLoading, cooldownSeconds, showOTPForm, initializeFromEmail, sendVerificationCode, resendCode, verifyCode } = useEmailVerification();

  const form = useForm<Step2FormValues>({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      verificationCode: "",
    },
  });

  // Initialisation basée sur la session et l'état de vérification
  useEffect(() => {
    if (!session?.user?.email) return;

    // Si l'email est déjà vérifié, rediriger
    if (session.user.emailVerified) {
      if (session.user.onboarding === "step2") {
        updateOnboardingStep("step3");
      }
      return;
    }

    // Initialiser l'état basé sur les données stockées
    initializeFromEmail(session.user.email);
  }, [session?.user, updateOnboardingStep, initializeFromEmail]);

  // Envoyer le code de vérification
  const handleSendCode = async () => {
    if (!session?.user.email) {
      toast.error("Email non trouvé");
      return;
    }

    await sendVerificationCode(session.user.email);
  };

  // Renvoyer le code
  const handleResendCode = async () => {
    if (!session?.user.email) return;
    await resendCode(session.user.email);
  };

  // Vérifier le code OTP
  const onSubmit = async (data: Step2FormValues) => {
    if (!session?.user.email) return;

    const success = await verifyCode(session.user.email, data.verificationCode, () => {
      updateOnboardingStep("step3");
    });

    // Réinitialiser le champ si la vérification échoue
    if (!success) {
      form.resetField("verificationCode");
    }
  };

  return (
    <FormShell
      title="Vérification de votre email"
      subtitle={
        <>
          <p className="text-sm text-balance text-muted-foreground">
            {showOTPForm
              ? "Saisissez le code de vérification envoyé à votre email."
              : "Cliquez sur le bouton ci-dessous pour recevoir un code de vérification."}
          </p>
          {session?.user?.email && <p className="text-sm font-medium text-muted-foreground">{session.user.email}</p>}
        </>
      }
      onSubmit={showOTPForm ? form.handleSubmit(onSubmit) : undefined}
    >
      {!showOTPForm ? (
        // Bouton initial pour envoyer l'email
        <div className="flex flex-col gap-4">
          <Button onClick={handleSendCode} disabled={isLoading} className="w-full" size="lg">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Envoyer le code
              </>
            )}
          </Button>
        </div>
      ) : (
        // Formulaire OTP
        <Form {...form}>
          <div className="grid gap-6">
            <FormField
              control={form.control}
              name="verificationCode"
              render={({ field }) => (
                <FormItem className="gap-4">
                  <FormLabel>Code de vérification</FormLabel>
                  <FormControl className="grid gap-3">
                    <InputOTP
                      maxLength={6}
                      pattern={REGEXP_ONLY_DIGITS}
                      {...field}
                      onComplete={() => form.handleSubmit(onSubmit)()}
                      autoFocus
                      autoComplete="off"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup>
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormMessage />
                  <Button type="button" variant="link" className="mt-2" onClick={handleResendCode} disabled={cooldownSeconds > 0}>
                    {cooldownSeconds > 0 ? `Renvoyer le code (${cooldownSeconds}s)` : "Renvoyer le code"}
                  </Button>
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Vérification...
                </>
              ) : (
                "Vérifier"
              )}
            </Button>
          </div>
        </Form>
      )}
    </FormShell>
  );
}
